/**
 * What each priority rating actually buys — the service level, stated plainly.
 *
 * ⚠️ THE WORD "MODEL" IS GONE FROM THIS TAB (Theresa, Sep 25 2026). Stakeholders read this page to
 * find out what their open roles get from TA, and "the effort model says 6.5 h" means nothing to
 * anybody who was not in the room when it was calibrated. Same numbers, named for the reader: an
 * agreed MINIMUM per role per week, and what TA owns at that rating.
 *
 * ⚠️ IT IS READ-ONLY HERE, and the editor lives on Planning → TA capacity planning. Two places to
 * change what a rating promises would eventually disagree, and the promise is TA's to set while
 * this page is where the rest of the company reads it.
 *
 * The hours come from the same `tierHours` every figure on the tab is computed from, so the card
 * cannot drift from the bar above it.
 */
import { RATINGS, type EffortModel, type Rating } from '../../../types/rolePrioritisation';
import { hoursLabel, tierHours } from '../../../utils/rolePrioritisation';
import { Card, RatingBadge, SectionHead } from './prioritisationUi';

/**
 * What TA owns at each rating — Theresa's wording (Sep 2026), shortened on Sep 29 2026 to fit one
 * tile each: "TA owns" is said once in the section head instead of four times, and "full TA
 * ownership of pipeline management" became "Pipeline". The commitment itself is unchanged.
 *
 * Deliberately not derived from the activity breakdown behind the hours: the hours say how MUCH,
 * these say WHAT, and a sentence generated from five activity numbers would read as arithmetic
 * rather than as the commitment it is.
 */
const OWNERSHIP: Record<Rating, string> = {
  AAA: 'Pipeline, first interviews and sourcing — plus cross-cluster support on extra pipeline initiatives.',
  A: 'Pipeline, first interviews and sourcing.',
  B: 'Pipeline, plus interviewing or sourcing — or both, shared with the hiring team.',
  C: 'Pipeline only. No interviewing, no sourcing.',
};

/**
 * ⚠️ ONE ROW OF FOUR TILES, NOT A TABLE (Theresa, Sep 29 2026). The table stood four tall rows high
 * and pushed §04's list of rated roles below the first screen, which is the thing a stakeholder
 * opens this page to find. The same three facts per rating, side by side; 2×2 below `sm`.
 */
export function ServiceLevels({ model }: { model: EffortModel }) {
  return (
    <section>
      <SectionHead
        number="02"
        title="What each priority gets"
        aside="the agreed minimum per role, per week, and what TA owns"
      />
      <Card>
        {/* The 1px gap over a `pearl` ground IS the divider, so it is right in both layouts without
            per-breakpoint border rules (`max-sm:` does not compile against this Tailwind config).
            `list-none m-0 p-0` because the app has no list reset: without it the tiles carried bullets
            and the list's left padding showed as a grey block through the gap. */}
        <ul className="m-0 grid list-none grid-cols-2 gap-px bg-pearl p-0 sm:grid-cols-4">
          {RATINGS.map((r) => (
            <li
              key={r}
              data-testid={`service-level-${r}`}
              className="bg-white px-3 py-2.5"
            >
              <div className="flex items-center gap-2">
                <RatingBadge rating={r} />
                <span className="text-sm font-semibold tabular-nums text-black">
                  {hoursLabel(tierHours(model, r))} h
                </span>
              </div>
              <p className="mt-1 text-xs leading-4 text-asphalt">{OWNERSHIP[r]}</p>
            </li>
          ))}
        </ul>
      </Card>
    </section>
  );
}
